import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import type {
  AssignTemporaryGroupPayload,
  BulkReportingLinesPayload,
  SetReportingLinePayload,
  TemporaryGroupRefPayload,
} from '@gokrew/models/reporting';
import { useAuth } from '@/context/AuthContext';
import {
  applyReportingLines,
  assignTemporaryGroup,
  getManagerOptions,
  getOrgChart,
  getReportingLine,
  getReportingLineHistory,
  getReportingSuggestions,
  getTemporaryGroups,
  makeTemporaryGroupPermanent,
  setReportingLine,
} from '@/api/reporting';
import { toastIfForbidden } from '@/utils/performance';
import { groupChangedDetails } from '@/utils/reporting';

type QC = ReturnType<typeof useQueryClient>;

/**
 * A structure change can move anyone's line, team size, temporary groups,
 * where their leave goes and the suggestions list, so every write refreshes
 * all reporting reads of the company.
 */
export const invalidateReporting = (qc: QC, companyId: string) => {
  for (const key of [
    QueryKeys.ReportingLine,
    QueryKeys.ReportingLineHistory,
    QueryKeys.ReportingOrgChart,
    QueryKeys.ReportingSuggestions,
    QueryKeys.ReportingTemporaryGroups,
    QueryKeys.LeaveRouting,
    QueryKeys.HandoverExceptions,
  ]) {
    qc.invalidateQueries({ queryKey: [key, companyId] });
  }
};

export const useReportingLine = (
  employeeId: string | undefined,
  enabled = true
) => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.ReportingLine, companyId, employeeId],
    queryFn: () => getReportingLine(companyId!, employeeId!),
    enabled: enabled && !!companyId && !!employeeId,
    select: (res) => res.data,
  });
};

export const useReportingLineHistory = (
  employeeId: string | undefined,
  enabled = true
) => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.ReportingLineHistory, companyId, employeeId],
    queryFn: () => getReportingLineHistory(companyId!, employeeId!),
    enabled: enabled && !!companyId && !!employeeId,
    select: (res) => res.data ?? [],
  });
};

export const useOrgChart = (enabled = true) => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.ReportingOrgChart, companyId],
    queryFn: () => getOrgChart(companyId!),
    enabled: enabled && !!companyId,
    select: (res) => res.data ?? [],
  });
};

export const useReportingSuggestions = () => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.ReportingSuggestions, companyId],
    queryFn: () => getReportingSuggestions(companyId!),
    enabled: !!companyId,
    select: (res) => res.data ?? [],
  });
};

export const useManagerOptions = (
  search: string,
  excludeEmployeeId: string | undefined,
  enabled: boolean
) => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [
      QueryKeys.ReportingManagerOptions,
      companyId,
      search,
      excludeEmployeeId,
    ],
    queryFn: () =>
      getManagerOptions(companyId!, {
        search: search || undefined,
        excludeEmployeeId,
        limit: 20,
      }),
    enabled: enabled && !!companyId,
    select: (res) => res.data ?? [],
  });
};

export const useSetReportingLine = (employeeId: string) => {
  const { companyId } = useAuth();
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.Employee);
  return useMutation({
    mutationFn: (payload: SetReportingLinePayload) =>
      setReportingLine(companyId!, employeeId, payload),
    onSuccess: () => {
      invalidateReporting(qc, companyId!);
      toast.success(t('reporting_line.toast.updated'));
    },
    onError: (error) =>
      toastIfForbidden(error, t('reporting_line.toast.forbidden')),
  });
};

export const useApplyReportingLines = () => {
  const { companyId } = useAuth();
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.Employee);
  return useMutation({
    mutationFn: (payload: BulkReportingLinesPayload) =>
      applyReportingLines(companyId!, payload),
    onSuccess: (res) => {
      invalidateReporting(qc, companyId!);
      const { applied = 0, failed = 0 } = res.data ?? {};
      if (failed) {
        toast.error(
          t('reporting_line.toast.bulk_partial', { applied, failed })
        );
      } else {
        toast.success(t('reporting_line.toast.bulk_applied', { applied }));
      }
    },
    onError: (error) =>
      toastIfForbidden(error, t('reporting_line.toast.forbidden')),
  });
};

/** Temporary groups in the admin's branches (spec 3.4). */
export const useTemporaryGroups = (enabled = true) => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.ReportingTemporaryGroups, companyId],
    queryFn: () => getTemporaryGroups(companyId!),
    enabled: enabled && !!companyId,
    select: (res) => res.data ?? [],
  });
};

/**
 * Moves a temporary group to a new manager. On a 409 `reporting.groupChanged`
 * the caller refreshes the member list from its `errorDetails` and explains
 * inline (the API client skips its toast). A group left with nobody drops out
 * of the refetched list, taking that inline warning with it, so that case is
 * toasted here.
 */
export const useAssignTemporaryGroup = () => {
  const { companyId } = useAuth();
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.Employee);
  return useMutation({
    mutationFn: (payload: AssignTemporaryGroupPayload) =>
      assignTemporaryGroup(companyId!, payload),
    onSuccess: (res) => {
      invalidateReporting(qc, companyId!);
      toast.success(
        t('reporting_line.groups.toast.moved', {
          count: res.data?.moved ?? 0,
        })
      );
    },
    onError: (error) => {
      invalidateReporting(qc, companyId!);
      if (groupChangedDetails(error)?.members.length === 0) {
        toast.error(t('reporting_line.groups.toast.emptied'));
        return;
      }
      toastIfForbidden(error, t('reporting_line.toast.forbidden'));
    },
  });
};

/** "Keep with {{cover}}": the cover becomes the permanent manager. */
export const useMakeTemporaryGroupPermanent = () => {
  const { companyId } = useAuth();
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.Employee);
  return useMutation({
    mutationFn: (payload: TemporaryGroupRefPayload) =>
      makeTemporaryGroupPermanent(companyId!, payload),
    onSuccess: (res) => {
      invalidateReporting(qc, companyId!);
      toast.success(
        t('reporting_line.groups.toast.made_permanent', {
          count: res.data?.moved ?? 0,
        })
      );
    },
    onError: (error) => {
      invalidateReporting(qc, companyId!);
      toastIfForbidden(error, t('reporting_line.toast.forbidden'));
    },
  });
};
